import type { ReactElement } from 'react';

import LinkWithQuery from 'src/app/components/Elements/LinkWithQuery';
import { DefaultPhoneNumber } from 'src/app/components/Elements/Location';
import type { PharmacyReasonProps } from 'src/app/components/PageSections/WhyPeopleLoveCapsule';
import { getStarted, howItWorks } from 'src/app/router/paths';
import type { IconTextItem } from 'src/app/types';
import { CAPSULE_HELP_EMAIL } from 'src/consts';

import { InlineBlockContent, RSymbol } from './styles';

// TODO: PAR-645 refactor all string promo codes to read from this constants file (maybe use enum)
export const OTC_COPY_VERSION_MAP: { [promo: string]: string } = {
  '15-off-essentials': 'default',
  sakara: 'no_offer',
  ushgnyc: 'ushgnyc',
  rapidsos: 'rapidsos',
  fairstead: 'fairstead',
  'redesign-health': 'redesign_health',
  kustomer: 'kustomer',
  newlab: 'newlab',
  flexdelivery: 'flexdelivery',
  devoted: 'devoted',
  cigna: 'cigna',
  morganstanley: 'morganstanley',
  ehehealth: 'ehehealth',
  blockrenovation: 'blockrenovation',
  somethingnavy: 'somethingnavy',
  lmc: 'lmc',
  'at-home-essentials': 'cws',
  'at-home': 'cws_no_offer',
  'lmc-offer': 'lmc_offer',
  'try-capsule-for-free': 'tryforfree',
  'try-capsule': 'trycapsule',
  ps198: 'ps198',
  cayugacenters: 'cayugacenters',
  cca: 'cca',
  chicagotransit: 'chicagotransit',
  'free-advil': 'freeadvil',
  redwoodcapital: 'redwoodcapital',
  dundermifflin: 'dundermifflin',
  ritual: 'ritual',
  'ritual-men': 'ritual_men',
};

/**
 * Promocodes to be used with multiple referrals
 * https://capsulerx.atlassian.net/wiki/spaces/CA/pages/2904555521/Using+multiple+referrals+on+promo+landing+pages
 */
export enum MultiReferralPromo {
  flexdelivery = 'flexdelivery',
}

/**
 * Promo codes that do not apply credit
 */
export const NON_CREDIT_PROMOS = [
  'cca',
  'cigna',
  'devoted',
  MultiReferralPromo.flexdelivery,
  'newlab',
  'redwoodcapital',
  'sakara',
  'try-capsule',
];

export const ELEMENT_IDS = {
  OTC: 'otc',
  firstField: 'firstField',
};

export const defaultPharmacyContent = [
  {
    title: 'Free same-day delivery',
    text: 'We deliver to your door, so you can stay home and stay safe.',
  },
  {
    title: 'No minimums for essentials',
    text: 'Stay healthy and prepared with common essentials for allergy, cold and flu, or pain and fever relief.',
  },
  {
    title: 'Never run out of refills',
    text: 'Send your prescriptions over to Capsule. Our predictive inventory software means you will never run out of meds.',
  },
  {
    title: 'We accept your insurance',
    text: 'We accept all insurance plans, and coordinate with them to get you the best price on your prescription medications.',
  },
];

export const employerPharmacyContent = [
  {
    title: 'Faster.',
    text: 'Never wait in line for the pharmacy. We bring your medications to your door or office, or wherever you are.',
  },
  {
    title: 'Smarter.',
    text: 'Never run out of refills. We keep track so you don’t have to.',
  },
  {
    title: 'Kinder.',
    text: 'Never talk to a voicemail autobot. Text, call, email, or chat with your pharmacist whenever is convenient for you.',
  },
  {
    title: 'Better.',
    text: 'Never speak to an insurance company again. We‘ll coordinate with your doctor and insurance.',
  },
];

export const ccaPharmacyContent = [
  {
    title: 'Faster.',
    text: 'Never wait in line for the pharmacy. We bring your medications to your door or office, or wherever you are.',
  },
  {
    title: 'Smarter.',
    text: 'Never run out of refills. We keep track so you don’t have to.',
  },
  {
    title: 'Kinder.',
    text: 'Never talk to a voicemail autobot. Text, call, email, or chat with your pharmacist whenever is convenient for you.',
  },
  {
    title: 'Better.',
    text: 'We‘ll coordinate with your doctor and insurance.',
  },
];

export const getDefaultQuestionContent = () => [
  {
    question: 'How much does it cost?',
    answer:
      'Capsule is free to use.  Delivery is always free, no minimums ever. You only pay your copay on your medication.',
  },
  {
    question: 'Do you take my insurance? ',
    answer:
      'Yes. Capsule accepts all commercial insurance plans, Managed Medicaid, and Medicare Part D. We’ll also coordinate directly with your insurance so you don’t have to.',
  },
  {
    question: 'Do you carry over the counter medication?',
    answer:
      'Yes. You can order any medication in our Essentials store, and free same-day delivery still applies. Or add items to your cart when checking out with a prescription.',
  },
  {
    question: 'Does Capsule deliver to me?',
    answer: (
      <>
        Find out <a href={'/get-started'}>here</a>.
      </>
    ),
  },
  {
    question: 'Can all my doctors use it?',
    answer: 'Yes. Any doctor can send a prescription to Capsule.',
  },
  {
    question: 'I have more questions.',
    answer: (
      <>
        Text us at <DefaultPhoneNumber /> or email{' '}
        <a className="red" href={`mailto:${CAPSULE_HELP_EMAIL}`}>
          {CAPSULE_HELP_EMAIL}
        </a>
        .
      </>
    ),
  },
];

const defaultOTCItems = [
  {
    title: 'Advil',
    price: '6.49',
    strength: '200mg / 24',
    src: '/public/advil.png',
    markedPrice: 'FREE',
  },
  {
    title: 'Benadryl',
    price: '9.99',
    strength: '25mg / 24',
    src: '/public/benadryl.png',
    markedPrice: 'FREE',
  },
  {
    title: 'Tylenol',
    price: '7.99',
    strength: '500mg / 24',
    src: '/public/tylenol.png',
    markedPrice: 'FREE',
  },
  {
    title: 'Band-Aids',
    price: '5.99',
    strength: '30 count',
    src: '/public/band-aid.png',
    markedPrice: 'FREE',
  },
  {
    title: 'Neosporin',
    price: '7.99',
    strength: '0.5oz',
    src: '/public/neosporin.png',
    markedPrice: 'FREE',
  },
  {
    title: 'Flonase',
    price: '17.49',
    strength: '0.34oz / 60',
    src: '/public/flonase.png',
    markedPrice: '$2.49',
  },
];

export const getEmployerAdditionalQuestionContent = () => [
  {
    question: 'Does Capsule deliver to me?',
    answer: (
      <>
        Capsule delivers the same day for free in many metro-areas and nearby suburbs. Check if your ZIP code, home
        and/or office, is available for same-day delivery -<a href={'/get-started'}>here</a>.
      </>
    ),
  },
];

interface QuestionAnswerSection {
  question: string;
  answer: string | Element | ReactElement;
}

interface OTCItemSection {
  title: string;
  price: string;
  strength: string;
  src: string;
  markedPrice?: string | null;
}

export interface OTCCopyProps {
  [promoCode: string]: {
    isEmployerOffering: boolean;
    formHeader: string | ReactElement;
    /**
     * formSubHeader: optional subheader below the form header
     */
    formSubHeader?: string | ReactElement | null;
    /**
     * formCaption: optional caption at the bottom of the section
     */
    formCaption?: string | ReactElement | null;
    offerSummary?: string | null;
    buttonLabel: string;
    explainerSectionType?: string | null;
    mobileCtaLabel: string;
    pharmacySectionHeader?: string;
    faqSectionHeader: string | Element;
    disclaimerLabel: string;
    /**
     * overrideQuestionsSection: optional, if present overrides all default FAQ's and becomes the
     * only FAQ questions
     */
    overrideQuestionsSection?: QuestionAnswerSection[];
    /**
     * additionalQuestionsSection: optional, questions are returned in addition to
     * employerAdditionalQuestionContent and defaultQuestionContent for FAQ
     */
    additionalQuestionsSection?: QuestionAnswerSection[];
    otcItems?: OTCItemSection[];
    additionalPharmacySection?: PharmacyReasonProps;
    hideAsSeenInSection?: boolean;
    hidePharmacySection?: boolean;
    hideFAQ?: boolean;
    expandFirstFAQ?: boolean;
    /**
     * isGetStartedCTA: optional, redirects to get started onboarding flow instead
     * of rendering sign up form if true
     */
    isGetStartedCTA?: boolean;
  };
}

export const getOTCCopy = (): OTCCopyProps => ({
  default: {
    isEmployerOffering: false,
    formHeader: 'We’ve partnered with Capsule to make your life easier.',
    formCaption: null,
    offerSummary: '$15 off over-the-counter items',
    buttonLabel: 'redeem offer',
    mobileCtaLabel: 'Get $15 credit',
    pharmacySectionHeader: 'Capsule is a full-service pharmacy',
    faqSectionHeader: 'Need a hand? We’re here to help.',
    disclaimerLabel:
      '*This offer is only eligible for first-time customers. Offer is valid for 14 days and can only be used in our Essentials store.',
    additionalQuestionsSection: [],
    otcItems: defaultOTCItems,
  },
  trycapsule: {
    isEmployerOffering: false,
    formHeader: 'Capsule is the simpler, smarter, kinder pharmacy',
    formCaption: null,
    offerSummary: '$10 off over-the-counter items',
    buttonLabel: 'redeem offer',
    mobileCtaLabel: 'Get $10 credit',
    faqSectionHeader: 'Need a hand? We’re here to help.',
    disclaimerLabel:
      '*This offer is only eligible for first-time customers. Offer is valid for 14 days and can only be used in our Essentials store.',
    additionalQuestionsSection: [],
    otcItems: defaultOTCItems,
    hidePharmacySection: true,
  },
  tryforfree: {
    isEmployerOffering: false,
    formHeader: 'Get $15 off Advil, Claritin, and other essentials.',
    formCaption: '',
    offerSummary: '$15 off over-the-counter items',
    buttonLabel: 'redeem offer',
    mobileCtaLabel: 'Get $15 credit',
    pharmacySectionHeader: 'Capsule is a full-service pharmacy',
    faqSectionHeader: 'Need a hand? We’re here to help.',
    disclaimerLabel:
      '*This offer is only eligible for first-time customers. Offer is valid for 14 days and can only be used in our Essentials store.',
    additionalQuestionsSection: [],
    otcItems: defaultOTCItems,
  },
  freeadvil: {
    isEmployerOffering: false,
    formHeader: 'Get a free bottle of Advil delivered to your door.',
    formCaption: null,
    offerSummary: 'Free Advil',
    buttonLabel: 'redeem offer',
    mobileCtaLabel: 'Get a free advil',
    pharmacySectionHeader: 'Capsule is a full-service pharmacy',
    faqSectionHeader: 'Need a hand? We’re here to help.',
    disclaimerLabel:
      '*This offer is only eligible for first-time customers in New York City. Offer is valid for 14 days and can only be used in our Essentials store.',
    additionalQuestionsSection: [],
    otcItems: defaultOTCItems,
  },
  no_offer: {
    isEmployerOffering: false,
    formHeader: 'Get your meds when you need them.',
    formCaption:
      'We’re the pharmacy that delivers your prescriptions and over the counter meds, the same day, for free.',
    offerSummary: null,
    buttonLabel: 'Get Started',
    mobileCtaLabel: 'Get Started',
    pharmacySectionHeader: 'Capsule is a full-service pharmacy',
    faqSectionHeader: 'Need a hand? We’re here to help.',
    disclaimerLabel: '',
    explainerSectionType: 'steps',
    additionalQuestionsSection: [],
    otcItems: [
      {
        title: 'Sakara Complete Probiotic Formula',
        price: '46.00',
        strength: '180 capsules',
        src: '/public/Sakara-Probiotic.png',
        markedPrice: null,
      },
      {
        title: 'Sakara the Foundation',
        price: '120.00',
        strength: '30 day supply',
        src: '/public/Sakara-Foundation.png',
        markedPrice: null,
      },
      {
        title: 'Sakara the Foundation: Prenatal',
        price: '130.00',
        strength: '30 day supply',
        src: '/public/Sakara-Prenatal.png',
        markedPrice: null,
      },
    ],
  },
  flexdelivery: {
    isEmployerOffering: false,
    isGetStartedCTA: true,
    formHeader: (
      <>
        Meds <InlineBlockContent>made easy</InlineBlockContent>
      </>
    ),
    caption: null,
    formSubHeader: (
      <>
        Exclusive flexible delivery from Express Scripts<RSymbol>&#174;</RSymbol> Pharmacy{' '}
        <InlineBlockContent>and Capsule</InlineBlockContent>
      </>
    ),
    offerSummary: null,
    buttonLabel: 'Sign Up',
    mobileCtaLabel: 'Sign Up',
    overrideQuestionsSection: [
      {
        question: 'What is the Flexible Delivery Program?',
        answer: (
          <>
            Express Scripts<RSymbol>&#174;</RSymbol> Pharmacy and Capsule have teamed up to give you multiple free
            delivery options to receive all of your prescriptions. You can choose same day, hand delivery of your
            prescriptions, or you can request hand delivery on a day of your choosing. There just needs to be someone –
            a friend, a doorman, a colleague – to receive your medication when it arrives. You can also opt to receive
            prescriptions by mail in 3-5 days. You do not need to be present to receive your prescriptions by mail.
          </>
        ),
      },
      {
        question: 'Why did my health plan offer the Flexible Delivery Program?',
        answer: (
          <>
            Your health plan wants to make your life easier. Through the Flexible Delivery Program, you can get your
            medications hand delivered or in the mail – you choose what works best for you!
          </>
        ),
      },
      {
        question: 'What medications can I receive through this program?',
        answer: (
          <>
            Same-day delivery is available for all medications. For controlled substances, we will need to verify your
            identification before we deliver. Mail delivery is available for select maintenance medications.
          </>
        ),
      },
      {
        question: 'How do I get started with a new prescription?',
        answer: (
          <>
            Ask your doctor to e-prescribe to: Capsule – Philadelphia or call (888) 910-1808. Every doctor can send
            prescriptions to Capsule. Once it’s ready, we’ll send you a text to set up your payment and schedule your
            free same-day delivery. It’s that simple.{' '}
            <a href="https://capsule.com/how-it-works" blank="_blank" rel="noopener noreferrer">
              Learn more about sending a new prescription to Capsule.
            </a>
          </>
        ),
      },
      {
        question: 'How do I transfer refills from a non-Express Scripts® Pharmacy to the Flexible Delivery Program?',
        answer: (
          <>
            Just provide us with some information about your medications and we’ll take care of the rest. We contact
            your old pharmacy, get your insurance information, and transfer your refills. When it’s time for your next
            refill, we’ll text you to set up your payment and schedule your free delivery, either hand delivered or by
            mail, your choice. We’ll do the work for you.{' '}
            <a href="https://capsule.com/get-started" blank="_blank" rel="noopener noreferrer">
              {' '}
              Get started now.{' '}
            </a>
          </>
        ),
      },
      {
        question: 'Which pharmacy is filling my prescriptions?',
        answer: (
          <>
            For new prescriptions, Capsule fills the prescription at their pharmacy. For delivered refills, either
            Capsule or Express Scripts® Pharmacy will fill the prescriptions. For mail delivery, prescriptions are
            filled at Express Scripts® Pharmacy.
          </>
        ),
      },
      {
        question: 'How much does the Flexible Delivery Program cost? Is delivery really free?',
        answer: (
          <>
            Yes! Our delivery is free so you only pay your copay or cost share. We will always let you know how much
            your prescription costs before you schedule your delivery.
          </>
        ),
      },
      {
        question: 'Where can I receive same-day delivery?',
        answer: (
          <>
            Capsule hand delivers your medication anywhere in the Greater Philadelphia region, including southern New
            Jersey. You can check your zip code{' '}
            <a href="https://capsule.com/get-started" blank="_blank" rel="noopener noreferrer">
              {' '}
              here
            </a>
            .
          </>
        ),
      },
      {
        question: 'What if I’m outside of the same-day delivery zone?',
        answer: (
          <>
            You have the option to receive your prescriptions by mail with no delivery fees. Call the number on the back
            of your member ID card to get started. Mail delivery is available for refills of select medications.
          </>
        ),
      },
      {
        question: 'Who will deliver my same-day medication?',
        answer: (
          <>
            All Capsule couriers undergo rigorous background checks and training, including the DOT Commercial Bicyclist
            Safety Course and HIPAA security training. To ensure your privacy, your medication will arrive in a
            discreet, tamper sealed package and only you and your pharmacist will know what’s inside.
          </>
        ),
      },
      {
        question: 'Can I order for a family member that is on my plan?',
        answer: (
          <>
            Yes. It’s easy to manage your family’s medication. Ask their doctor to{' '}
            <a href="https://capsule.com/how-it-works" blank="_blank" rel="noopener noreferrer">
              send new prescriptions
            </a>{' '}
            directly to Capsule or{' '}
            <a href="https://capsule.com/get-started" blank="_blank" rel="noopener noreferrer">
              transfer your family’s refills to Capsule.
            </a>{' '}
            We’ll text you to setup your payment and schedule your free delivery.
          </>
        ),
      },
      {
        question: 'Is my information secure?',
        answer: (
          <>
            Your information is always safe with the Flexible Delivery Program. We store all of your personal data in an
            encrypted HIPAA-compliant environment.
          </>
        ),
      },
      {
        question: 'What if my prescription requires a prior authorization?',
        answer: (
          <>
            We work with your doctor and insurance company to quickly and efficiently handle your prior authorization.
            We’ll keep you updated so you know exactly how long the process will take.
          </>
        ),
      },
      {
        question: 'How can I get an FSA receipt?',
        answer: (
          <>
            To get a copy of your FSA receipt, sign in to Capsule via the iOS app or website and click &ldquo;Your
            Orders&rdquo; from the navigation menu. Open the order for which you need a receipt and click &ldquo;View
            Receipt,&rdquo; where you will be able to export your FSA receipt as a PDF.
          </>
        ),
      },
    ],

    pharmacySectionHeader: null,
    additionalPharmacySection: null,
    hideAsSeenInSection: true,
    hidePharmacySection: true,
    faqSectionHeader: 'Need a hand? We’re here to help.',
    disclaimerLabel: '',
    explainerSectionType: null,
  },
  devoted: {
    isEmployerOffering: false,
    isGetStartedCTA: true,
    formHeader: (
      <>
        Meds <InlineBlockContent>made easy</InlineBlockContent>
      </>
    ),
    caption: null,
    formSubHeader: <>A more conveniant pharmacy experience from Devoted Health and Capsule Pharmacy</>,
    offerSummary: null,
    buttonLabel: 'Sign Up',
    mobileCtaLabel: 'Sign Up',
    overrideQuestionsSection: [
      {
        question: 'How does Capsule Pharmacy work?',
        answer: (
          <>
            We will reach out to your old pharmacy to move your existing prescriptions and / or work with your
            provider(s) to send new prescriptions to Capsule. We fill your prescriptions in our pharmacy and then reach
            out to you to schedule your free delivery. We also provide a dedicated pharmacy team so if you ever have
            questions about your medications, even days after receiving your delivery, simply reach out and our team
            will be happy to assist you.
          </>
        ),
      },
      {
        question: 'Why is Devoted Health partnering with Capsule Pharmacy?',
        answer: (
          <>
            Devoted Health has partnered with Capsule Pharmacy to offer a more convenient pharmacy experience, including
            same-day delivery of your prescriptions at no additional cost.
          </>
        ),
      },
      {
        question: 'Do you accept my insurance?',
        answer: <>Yes, Capsule accepts all Devoted Health Medicare Advantage Plans.</>,
      },
      {
        question: 'How do I transfer refills from my old pharmacy to Capsule?',
        answer: (
          <>
            Just provide us with some information about your medications and we’ll take care of the rest. We contact
            your old pharmacy, get your insurance information, and transfer your refills. When it’s time for your next
            refill, we’ll reach out to you to set up your payment and schedule your free delivery. We’ll do the work for
            you.
            <br />
            <a href="https://capsule.com/get-started" blank="_blank" rel="noopener noreferrer">
              Get started now
            </a>
            .
          </>
        ),
      },
      {
        question: 'How do I get started with a new prescription?',
        answer: (
          <>
            Ask your doctor to e-prescribe to Capsule. Every doctor can send prescriptions to Capsule. Once it’s ready,
            we’ll send you a text to set up your payment and schedule your free same-day delivery. It’s that simple.
            <br />
            <LinkWithQuery to={howItWorks.toPath()}>
              Learn more about sending a new prescription to Capsule.
            </LinkWithQuery>
          </>
        ),
      },
      {
        question: 'How do I get my medication?',
        answer: (
          <>
            Capsule will deliver your medications by hand right to your door. We can also leave with the front desk or
            doorman if you live in an apartment, or meet you in the lobby. There just needs to be someone - you, a
            family member, friend, doorman - that you designate to receive your medication when it arrives, we can't
            just leave on the doorstep. There is no cost for this service.
          </>
        ),
      },
      {
        question: 'What if my prescription requires a prior authorization?',
        answer: (
          <>
            We work with your doctor and Devoted Health to quickly and efficiently handle your prior authorization.
            We’ll keep you updated so you know exactly how long the process will take.
          </>
        ),
      },
      {
        question: 'How much does using Capsule cost? Is delivery really free?',
        answer: (
          <>
            Yes! Our delivery is free so you only pay your copay or cost share. We will always let you know how much
            your prescription costs before you schedule your delivery.
          </>
        ),
      },
      {
        question: 'What medications can I get through Capsule?',
        answer: (
          <>
            We are a retail pharmacy, carrying the full inventory of a chain retail pharmacy, and same-day delivery is
            available for all medications. Additionally, we have an Essentials Store carrying the 60+ most common OTC
            (over-the-counter) medications that are also delivered same-day, at no additional cost.
          </>
        ),
      },
      {
        question: 'Do you deliver controlled substances?',
        answer: <>Yes. For controlled substances, we will need to verify your identification before we deliver.</>,
      },
      {
        question: 'Do I have to download the app to use Capsule?',
        answer: (
          <>
            No, you do not need to download the app to use Capsule. We will communicate with you via whatever method you
            prefer (phone, text, or app).
          </>
        ),
      },
      {
        question: 'Who will deliver my same-day medication?',
        answer: (
          <>
            All Capsule couriers undergo rigorous background checks and training, including HIPAA security training. To
            ensure your privacy, your medication will arrive in a discreet, tamper sealed package and only you and your
            pharmacist will know what’s inside.
          </>
        ),
      },
      {
        question: 'Is my information secure?',
        answer: (
          <>
            Your information is always safe with Capsule. We store all of your personal data in an encrypted
            HIPAA-compliant environment.
          </>
        ),
      },
    ],

    pharmacySectionHeader: null,
    additionalPharmacySection: null,
    hideAsSeenInSection: true,
    hidePharmacySection: true,
    faqSectionHeader: 'Need a hand? We’re here to help.',
    disclaimerLabel: '',
    explainerSectionType: null,
  },
  morganstanley: {
    isEmployerOffering: true,
    isGetStartedCTA: true,
    formHeader: 'Morgan Stanley exclusive benefits with Capsule pharmacy',
    formCaption: 'You focus on the markets — let us take care of your medications.',
    offerSummary: null,
    buttonLabel: 'Sign Up',
    mobileCtaLabel: 'Sign Up',
    pharmacySectionHeader: 'Capsule is a full-service pharmacy that is...',
    overrideQuestionsSection: [
      {
        question: 'Why did Morgan Stanley partner with Capsule?',
        answer: (
          <>
            Morgan Stanley wants to make your life easier. Capsule is the faster, kinder, and safer pharmacy that brings
            your medications straight to your door the same day for free.
          </>
        ),
      },
      {
        question: 'What’s the benefit to me?',
        answer: (
          <>
            No more hassle at the pharmacy, wasted time on the phone with insurance companies, or the burden of
            remembering your refills. Capsule makes it easy to get and manage your medication.
          </>
        ),
      },
      {
        question: 'How does Capsule work?',
        answer: (
          <>
            Sign up now in 15 seconds. We accept your insurance and fill your prescription, ensuring you get the best
            price on your medication. We text you to schedule free, same-day delivery.
          </>
        ),
      },
      {
        question: 'Is my information secure and private?',
        answer: (
          <>
            Your information is always safe with us. Capsule is an encrypted HIPAA-compliant environment.
            <br />
            <br />
            We absolutely never share personal information with your employer (or anyone else). As part of an employer
            partnership, you will be asked to verify your account with an employee email address simply to give you
            access to exclusive employee discounts.
          </>
        ),
      },
      {
        question: 'Does Capsule deliver to me?',
        answer: (
          <>
            Find out <LinkWithQuery to={getStarted.toPath()}>here</LinkWithQuery>. Please note: Capsule may be available
            in your area even if it is not listed on our public locations page. Try your ZIP code here or in the form
            above to confirm.
          </>
        ),
      },
      {
        question: 'Can my family use Capsule – and can I order medications for them?',
        answer: (
          <>
            Yes. It’s easy to manage your family’s medications with Capsule. Just ask your doctor to send new
            prescriptions to Capsule or move your family’s refills from their old pharmacy. We’ll text you to set up
            payment and schedule free delivery. We accept all major insurance plans.
          </>
        ),
      },
      {
        question: 'How much does it cost?',
        answer: (
          <>
            Capsule is free, just pay your copay. Capsule accepts all major insurance and your copay will not change. We
            will always let you know how much your prescription costs before you schedule your delivery.
          </>
        ),
      },
      {
        question: 'Can you save me money?',
        answer: <>We work with your doctor and insurance to get you the best price on your medication.</>,
      },
      {
        question: 'Does Capsule coordinate my refills too?',
        answer: <>Of course. We’ll never let you run out of your medications.</>,
      },
      {
        question: 'Can I use Capsule with my One Medical membership?',
        answer: (
          <>
            Yes. Every doctor can send prescriptions to Capsule. Ask your doctor to e-prescribe to: Capsule. Once it’s
            ready, we’ll send you a text to schedule your free delivery.
          </>
        ),
      },
      {
        question: 'Does Capsule offer COVID-19 at-home tests?',
        answer: (
          <>
            Yes. At-home COVID-19 tests are now available with same-day delivery in NYC and select cities. Once you’ve
            logged in to the Capsule app, navigate to the essentials store to add a test to your cart.
          </>
        ),
      },
    ],
    faqSectionHeader: 'Need a hand? We’re here to help.',
    disclaimerLabel: '* Exclusive to MS employees and family members only.',
    explainerSectionType: null,
  },
  dundermifflin: {
    isEmployerOffering: true,
    formHeader: "Get your free COVID-19 vaccine at Dunder Mifflin's Scranton office, and other pharmacy benefits",
    offerSummary: '10% off over-the-counter meds',
    buttonLabel: 'Sign Up',
    mobileCtaLabel: 'Get Started',
    pharmacySectionHeader: 'Capsule is a full-service pharmacy that is...',
    additionalQuestionsSection: [
      {
        question: 'Am I eligible to receive the vaccine?',
        answer: (
          <>
            <p>
              This vaccination program is exclusive to Dunder Mifflin Employees. All appointments require valid approval
              by Dwight Schrute, assistant to the regional manager.
            </p>
            <p>
              No walk-in appointments will be accepted. Dunder Mifflin employees must book an appointment in advance
              from this page.
            </p>
          </>
        ),
      },
      {
        question: 'What if I am not a Dunder Mifflin employee?',
        answer: (
          <>
            <p>
              Await announcements on your eligibility to get the vaccine by monitoring your state’s website to find
              local vaccination options.
            </p>
            <p>
              This vaccination program is exclusive to Dunder Mifflin employees. All appointments require a valid
              approval from Dwight Schrute, Assistant to the Regional Manager.
            </p>
            <p>
              No walk-in appointments will be accepted. Dunder Mifflin employees must book an appointment in advance
              from this page or from their Dunder Mifflin Pyramid.
            </p>
          </>
        ),
      },
      {
        question: 'How do I book an appointment and receive confirmation?',
        answer: (
          <>
            <p>
              You book an appointment from this page, simply by verifying that you are a Dunder Mifflin employee,
              creating a Capsule account, and booking your appointment. You also have the option to provide personal
              health information in advance of your appointment - this will make your appointment time shorter.
            </p>
            <p>Capsule will send an appointment confirmation to the email address you provide.</p>
          </>
        ),
      },
      {
        question: 'Who will administer the vaccine?',
        answer:
          'Capsule pharmacists are trained and certified immunizers in the state of Illinois and will administer the vaccine in accordance with federal, state, and local guidelines. Our pharmacists are also friendly and caring and take pleasure in looking after people.',
      },
      {
        question: 'When do I get my second vaccine dose?',
        answer:
          'Capsule will be using the Moderna vaccine, and your second dose appointment will be automatically scheduled for 28 days after your first dose, at the same location and time as your first dose appointment.',
      },
      {
        question: 'How much will the vaccine cost me?',
        answer:
          'You will not have to pay anything out of pocket to receive the vaccine. We will ask that you please bring proof of insurance coverage to your appointment. This allows Capsule to bill your insurance a small administration fee to cover the cost of our immunizers’ time and efforts to keep Chicagoans safe.',
      },
      {
        question: 'What if I do not have insurance?',
        answer: 'It is not a problem. If you are a Dunder Mifflin employee, you can still be vaccinated by Capsule.',
      },
      {
        question: 'What can I expect at the vaccine clinic?',
        answer: (
          <>
            <p>
              When you get to the vaccine clinic, we'll check your employee ID and ask you a few health screening
              questions to ensure you are a good match for the vaccine. Our immunization certified pharmacist will then
              give you the shot. We'll ask you to sit for observation for 15 minutes. The whole process will take ~35
              minutes.
            </p>
            <p>
              Our pharmacists will provide you with a vaccination card, recording the date and manufacturer of your
              vaccine. Take a picture of your vaccine card, in case you misplace it!
            </p>
          </>
        ),
      },
      {
        question: 'How can I prepare for my vaccine appointment?',
        answer:
          'Arrive 15 minutes early, bring your insurance card and photo ID, wear a mask, wear a short sleeve shirt, and stay home if you feel sick or have been exposed to COVID-19.',
      },
      {
        question: 'How does the vaccine work?',
        answer: (
          <>
            <p>
              The vaccines use mRNA to teach our cells how to make a protein that triggers an immune response. This
              immune response is triggered in your body ahead of any exposure to SARS-COV-2, the virus that causes
              COVID-19, creating a protective shield that defends against the illness.
            </p>
            <p>
              More information on how mRNA vaccines work can be found{' '}
              <a
                href={'https://www.cdc.gov/coronavirus/2019-ncov/vaccines/different-vaccines/mrna.html'}
                target="_blank"
              >
                here
              </a>
              .
            </p>
          </>
        ),
      },
      {
        question: 'Should I get the vaccine if I am pregnant or breastfeeding?',
        answer: (
          <>
            <p>
              According to{' '}
              <a
                href={'https://www.cdc.gov/coronavirus/2019-ncov/vaccines/recommendations/pregnancy.html'}
                target="_blank"
              >
                CDC guidelines
              </a>{' '}
              you should get the vaccine if pregnant or breastfeeding.
            </p>
            <p>
              There is currently no evidence that antibodies formed from COVID-19 vaccination cause any problems with
              pregnancy. However, there is currently little data on the safety of the vaccines in pregnant or
              breastfeeding women, as they were not included in the clinical trials.
            </p>
            <p>If you have questions, talk to your healthcare provider.</p>
          </>
        ),
      },
      {
        question: 'Should I get the vaccine if I have had cancer or am currently getting chemotherapy?',
        answer:
          'Immunocompromised people may receive the COVID-19 vaccination. Consult with your healthcare provider to make the best decision for you.',
      },
      {
        question: 'Should I get the vaccine if I have already had COVID-19 or currently have it?',
        answer: (
          <>
            <p>CDC guidance is to still get the vaccine even if you have already had COVID-19.</p>
            <p>
              If you currently have COVID-19, you should wait until you are fully recovered before getting the vaccine.
              Capsule will not allow anyone on site who is suffering from or has symptoms of COVID-19.
            </p>
          </>
        ),
      },
      {
        question: 'Who can I talk to about any questions after getting the vaccine? ',
        answer: (
          <>
            <p>If you think you are in a life threatening emergency, please contact 9-1-1.</p>
            <p>
              If you have any questions after getting the vaccine, contact one of our pharmacists. You will receive
              Capsule contact information along with your appointment confirmation.
            </p>
            <p>
              You can report side effects and reactions using the{' '}
              <a href={'https://www.cdc.gov/coronavirus/2019-ncov/vaccines/safety/vsafe.html'} target="_blank">
                CDC V-safe tool
              </a>
              .
            </p>
          </>
        ),
      },
      {
        question: 'What if I lose my vaccination card?',
        answer: (
          <>
            <p>We recommend taking a picture of your vaccination card for safe-keeping.</p>
            <p>
              If you do lose your card that includes your second appointment information, you can still receive your
              second dose. Capsule will send you appointment reminders for your second appointment.
            </p>
          </>
        ),
      },
      {
        question: 'Why get the vaccine from Capsule?',
        answer: (
          <>
            <p>
              Dunder Mifflin has partnered with Capsule to make the vaccine available and accessible to all employees.
              Through Capsule’s mobile appointment scheduling, automated reminders, and 7 days a week access to
              pharmacists by text, we make it easy for you to get vaccinated.
            </p>
            <p>
              Additionally, Capsule safely delivers prescriptions same-day, for free, all from your phone. Click{' '}
              <a href={'https://www.capsule.com/'} target="_blank">
                here
              </a>{' '}
              to get started.
            </p>
          </>
        ),
      },
    ],
    faqSectionHeader: 'Need a hand? We’re here to help.',
    disclaimerLabel:
      '*This program is exclusive to Dunder Mifflin employees. Proof of employee badge number and matching employee ID is required. Do not attempt to sign up if you are not a Dunder Mifflin employee. ',
    explainerSectionType: null,
  },
});

export const iconTextItems = (args: {
  offerSummary: string;
  promoCode: string;
  employeeDescription: string;
}): IconTextItem[] => {
  const { offerSummary, promoCode } = args;
  if (promoCode === 'free-advil') {
    return [
      { text: 'Capsule pharmacy delivers your prescription and non-prescription meds for free', iconSource: '' },
      {
        text: 'Enter your email for a complimentary Advil to try Capsule—no cost, no commitments.',
        iconSource: '',
      },
    ];
  } else if (promoCode === 'try-capsule-for-free') {
    return [
      { text: offerSummary, iconSource: '/public/pill_bottle_with_cost_tag.svg' },
      {
        text: 'Capsule pharmacy delivers your prescription and non-prescription meds for free',
        iconSource: '/public/branded_bag_with_hand.svg',
      },
    ];
  } else if (promoCode === 'devoted') {
    return [
      { text: 'Free same-day prescription delivery', iconSource: '/public/branded_bag_with_hand.svg' },
      { text: 'Devoted Health Medicare Advantage Plans accepted', iconSource: '/public/envelope.svg' },
      { text: 'Dedicated pharmacy team support', iconSource: '/public/message_icons.svg' },
    ];
  } else if (offerSummary === null) {
    return [
      { text: 'Free same-day prescription delivery', iconSource: '/public/branded_bag_with_hand.svg' },
      { text: 'Your insurance accepted', iconSource: '/public/envelope.svg' },
      { text: 'Call or text with pharmacists', iconSource: '/public/message_icons.svg' },
    ];
  }

  return [
    { text: offerSummary, iconSource: '/public/pill_bottle_with_cost_tag.svg' },
    { text: 'Free same-day prescription delivery', iconSource: '/public/branded_bag_with_hand.svg' },
    { text: 'Call or text with pharmacists', iconSource: '/public/message_icons.svg' },
    { text: 'Your insurance accepted', iconSource: '/public/envelope.svg' },
  ];
};
